import * as React from 'react';
import { CheckCircle2, KeyRound, Loader2, Save, Shield, ShieldOff } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  usePlatformConfig,
  useSetPlatformSecret,
  type PlatformConfig,
} from '../../hooks/useBilling';

const SECRET_GROUPS: Array<{
  title: string;
  description: string;
  items: Array<{
    name: string;
    label: string;
    placeholder?: string;
    statusKey: keyof PlatformConfig['status'];
    statusField?: string;
  }>;
}> = [
  {
    title: 'Mollie (Zahlungen)',
    description:
      'Der Mollie-Key bestimmt das Bezahlmodul der Anfänger-Modus-Kunden. Test-Keys beginnen mit `test_`, Live-Keys mit `live_`.',
    items: [
      { name: 'MOLLIE_API_KEY', label: 'Mollie API-Key', statusKey: 'mollie' },
      { name: 'MOLLIE_WEBHOOK_SECRET', label: 'Mollie Webhook-Secret (optional)', statusKey: 'mollie' },
    ],
  },
  {
    title: 'Generalkeys — KI-Anbieter',
    description:
      'Werden für Anfänger-Modus-Kunden eingesetzt. Der Endkunde sieht diese Keys nie.',
    items: [
      { name: 'PLATFORM_OPENAI_API_KEY', label: 'OpenAI', statusKey: 'openai' },
      { name: 'PLATFORM_ANTHROPIC_API_KEY', label: 'Anthropic Claude', statusKey: 'anthropic' },
      { name: 'PLATFORM_GOOGLE_AI_API_KEY', label: 'Google Gemini', statusKey: 'google' },
      { name: 'PLATFORM_GROQ_API_KEY', label: 'Groq', statusKey: 'groq' },
      { name: 'PLATFORM_ELEVENLABS_API_KEY', label: 'ElevenLabs (TTS)', statusKey: 'elevenlabs' },
      { name: 'PLATFORM_DEEPGRAM_API_KEY', label: 'Deepgram (STT)', statusKey: 'deepgram' },
    ],
  },
  {
    title: 'Generalkeys — Telefonie',
    description:
      'Werden für den Plattform-Nummern-Pool benutzt. Müssen ein eigener Twilio/Telnyx-Account sein, NICHT der eines Kunden.',
    items: [
      { name: 'PLATFORM_TWILIO_ACCOUNT_SID', label: 'Twilio Account-SID', statusKey: 'twilio' },
      { name: 'PLATFORM_TWILIO_AUTH_TOKEN', label: 'Twilio Auth-Token', statusKey: 'twilio' },
      { name: 'PLATFORM_TELNYX_API_KEY', label: 'Telnyx API-Key', statusKey: 'telnyx' },
    ],
  },
];

export function AdminPlatformKeysPage() {
  const config = usePlatformConfig();

  if (config.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
      </div>
    );
  }
  if (config.isError || !config.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Fehler: {(config.error as Error)?.message ?? 'Konnte Konfiguration nicht laden.'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Platform-Konfiguration</CardTitle>
          <CardDescription>
            Zentrale Schlüssel + Tarife für die SaaS-Anfänger-Variante. Werte werden in
            Google Secret Manager gespeichert — die UI zeigt nur ob ein Eintrag existiert,
            niemals den Wert selbst.
          </CardDescription>
        </CardHeader>
      </Card>

      {SECRET_GROUPS.map((g) => (
        <SecretGroupCard
          key={g.title}
          title={g.title}
          description={g.description}
          items={g.items}
          status={config.data.status}
        />
      ))}

      <Card>
        <CardHeader>
          <CardTitle>Wo wurden Tarife &amp; Steuersätze hin verschoben?</CardTitle>
          <CardDescription>
            Tarife (Anfänger-Kacheln) leben jetzt unter{' '}
            <strong>Mitgliedschafts-Pläne</strong> — Source-of-Truth für die zwei
            Anfänger-Pläne (Abo + Pay-as-you-go). Steuersätze sind unter{' '}
            <strong>Rechnungs-Einstellungen</strong> gewandert, weil sie
            ausschließlich Rechnungen betreffen.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Secret group card
// ─────────────────────────────────────────────────────────────────────

function SecretGroupCard({
  title,
  description,
  items,
  status,
}: {
  title: string;
  description: string;
  items: Array<{ name: string; label: string; statusKey: keyof PlatformConfig['status'] }>;
  status: PlatformConfig['status'];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Schlüssel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Wert setzen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const present = isSecretPresent(item.name, status);
              return (
                <TableRow key={item.name}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{item.label}</span>
                      <code className="text-[10px] text-muted-foreground">{item.name}</code>
                    </div>
                  </TableCell>
                  <TableCell>
                    {present ? (
                      <Badge variant="success">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> gesetzt
                      </Badge>
                    ) : (
                      <Badge variant="muted">leer</Badge>
                    )}
                    {item.name === 'MOLLIE_API_KEY' && status.mollie.hasApiKey && (
                      <Badge variant="outline" className="ml-1.5">
                        {status.mollie.mode === 'live' ? (
                          <>
                            <Shield className="mr-1 h-3 w-3" /> live
                          </>
                        ) : (
                          <>
                            <ShieldOff className="mr-1 h-3 w-3" /> test
                          </>
                        )}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <SecretInputRow name={item.name} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function isSecretPresent(name: string, status: PlatformConfig['status']): boolean {
  if (name === 'MOLLIE_API_KEY') return status.mollie.hasApiKey;
  if (name === 'MOLLIE_WEBHOOK_SECRET') return status.mollie.hasWebhookSecret;
  if (name === 'PLATFORM_OPENAI_API_KEY') return status.openai.hasApiKey;
  if (name === 'PLATFORM_ANTHROPIC_API_KEY') return status.anthropic.hasApiKey;
  if (name === 'PLATFORM_GOOGLE_AI_API_KEY') return status.google.hasApiKey;
  if (name === 'PLATFORM_GROQ_API_KEY') return status.groq.hasApiKey;
  if (name === 'PLATFORM_ELEVENLABS_API_KEY') return status.elevenlabs.hasApiKey;
  if (name === 'PLATFORM_DEEPGRAM_API_KEY') return status.deepgram.hasApiKey;
  if (name === 'PLATFORM_TWILIO_ACCOUNT_SID') return status.twilio.hasAccountSid;
  if (name === 'PLATFORM_TWILIO_AUTH_TOKEN') return status.twilio.hasAuthToken;
  if (name === 'PLATFORM_TELNYX_API_KEY') return status.telnyx.hasApiKey;
  return false;
}

function SecretInputRow({ name }: { name: string }) {
  const set = useSetPlatformSecret();
  const { toast } = useToast();
  const [value, setValue] = React.useState('');

  async function onSet() {
    if (!value.trim()) return;
    try {
      await set.mutateAsync({ name, value: value.trim() });
      toast({ title: 'Schlüssel gespeichert' });
      setValue('');
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Input
        type="password"
        placeholder="Neuen Wert eintragen …"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-8 max-w-xs font-mono text-xs"
      />
      <Button
        size="sm"
        onClick={() => void onSet()}
        disabled={set.isPending || !value.trim()}
      >
        {set.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

